import { Navigate, Route, Routes } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { useAuthStore } from "@/stores/auth";
import { useThemeStore } from "@/stores/theme";
import { LoginPage } from "@/pages/Login";
import { RegisterPage } from "@/pages/Register";
import { TwoFactorChallengePage } from "@/pages/TwoFactorChallenge";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { ResetPasswordPage } from "@/pages/ResetPassword";
import { VerifyEmailPage } from "@/pages/VerifyEmail";
import { OAuthReturnPage } from "@/pages/OAuthReturn";
import { NotFoundPage } from "@/pages/NotFound";
import { ToastSystem } from "@/components/Toast";
import { OnboardingOverlay } from "@/components/Onboarding";
import CookieConsent from "@/components/CookieConsent";

// Un seul paquet de 594 ko (173 ko compresse) etait servi pour TOUTE
// l'application: quelqu'un qui ouvre la page de connexion sur son telephone
// telechargeait l'editeur video entier -- timeline, lecteur, canvas -- pour
// voir un formulaire. Les pages du premier ecran (connexion, inscription,
// reinitialisation) restent immediates; le reste arrive a la demande.
const AppShell = lazy(() => import("@/components/AppShell").then((m) => ({ default: m.AppShell })));
const ProjectsPage = lazy(() => import("@/pages/Projects").then((m) => ({ default: m.ProjectsPage })));
const VideoTemplatesPage = lazy(() => import("@/pages/VideoTemplates").then((m) => ({ default: m.VideoTemplatesPage })));
const EditorPage = lazy(() => import("@/pages/Editor").then((m) => ({ default: m.EditorPage })));
const BillingPage = lazy(() => import("@/pages/Billing").then((m) => ({ default: m.BillingPage })));
const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.AccountPage })));
const AdminSettingsPage = lazy(() => import("@/pages/AdminSettings").then((m) => ({ default: m.AdminSettingsPage })));
const AdminAutomationPage = lazy(() => import("@/pages/AdminAutomation").then((m) => ({ default: m.AdminAutomationPage })));
const RenderHistoryPage = lazy(() => import("@/pages/RenderHistory").then((m) => ({ default: m.RenderHistoryPage })));
const BlogPage = lazy(() => import("@/pages/blog/BlogPage").then((m) => ({ default: m.BlogPage })));
const BlogArticlePage = lazy(() => import("@/pages/blog/BlogArticlePage").then((m) => ({ default: m.BlogArticlePage })));
const PrivacyPage = lazy(() => import("@/pages/legal/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage"));
const CookiesPage = lazy(() => import("@/pages/legal/CookiesPage"));
const LegalNoticePage = lazy(() => import("@/pages/legal/LegalNoticePage"));
const RefundPage = lazy(() => import("@/pages/legal/RefundPage"));

function EcranDAttente() {
  return (
    <div className="flex h-dvh items-center justify-center bg-ef-bg">
      <span
        className="h-8 w-8 animate-spin rounded-full border-2 border-ef-border border-t-ef-accent"
        role="status"
        aria-label="Chargement"
      />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const hydrated = useAuthStore((s) => s.hydrated);
  // Wait for the initial cookie-refresh to settle before deciding — otherwise a
  // hard reload on a protected route (e.g. the editor) bounces to /login even
  // though the session is valid.
  if (!hydrated) return <EcranDAttente />;
  if (!accessToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  const hydrate = useAuthStore((s) => s.hydrate);
  const accessToken = useAuthStore((s) => s.accessToken);
  const initTheme = useThemeStore((s) => s.init);

  useEffect(() => {
    // Apply the persisted accent preset before first paint of themed content.
    initTheme();
    // Best-effort: try to refresh the access token from the httpOnly cookie
    // on app boot. If the cookie is missing or expired we land on /login.
    void hydrate();
  }, [hydrate, initTheme]);

  return (
    <>
    <Suspense fallback={<EcranDAttente />}>
    <Routes>
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/2fa" element={<TwoFactorChallengePage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/oauth/return" element={<OAuthReturnPage />} />
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/cookies" element={<CookiesPage />} />
      <Route path="/legal/notice" element={<LegalNoticePage />} />
      <Route path="/legal/refund" element={<RefundPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/projects" replace />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/video-templates" element={<VideoTemplatesPage />} />
        <Route path="/projects/:projectId/editor" element={<EditorPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/renders" element={<RenderHistoryPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/admin/settings" element={<AdminSettingsPage />} />
        <Route path="/admin/automation" element={<AdminAutomationPage />} />
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    <ToastSystem />
    <OnboardingOverlay enabled={!!accessToken} />
    {/* Les valeurs par defaut du composant pointent vers /privacy et
        /cookies, qui n'existent pas: les routes sont sous /legal/. Les deux
        liens du bandeau -- obligatoire, vu par chaque premier visiteur --
        tombaient donc sur la page 404. */}
    <CookieConsent privacyPath="/legal/privacy" cookiesPath="/legal/cookies" />
    </>
  );
}
